import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, DollarSign, Clock, Users, Percent, Gift, Image } from "lucide-react";
import { PhotoGallery } from "@/components/ui/PhotoGallery";
import { PhotoParentType } from "@/types/photos";
import { usePhotos } from "@/hooks/usePhotos";
import type { ServiceOptionResponse } from "@/types/services";
import EditServiceOptionModal from "../modals/EditServiceOptionModal";

interface ServiceOptionDetailDrawerProps {
  serviceOption: ServiceOptionResponse;
  open: boolean;
  onClose: () => void;
}

export default function ServiceOptionDetailDrawer({ serviceOption, open, onClose }: ServiceOptionDetailDrawerProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const { photos } = usePhotos(PhotoParentType.ServiceOption, serviceOption.id);

  const formatPrice = (price?: number) => {
    if (!price) return "Free";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: serviceOption.currency || 'AED'
    }).format(price);
  };

  const getPricingDisplay = () => {
    if (serviceOption.pricingModel === 'PerPerson') {
      return `${formatPrice(serviceOption.basePrice)}/person`;
    }
    return formatPrice(serviceOption.basePrice);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <div className="flex items-start justify-between">
              <div>
                <SheetTitle className="text-xl">{serviceOption.name}</SheetTitle>
                <SheetDescription>
                  Option ID: {serviceOption.id}
                </SheetDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={serviceOption.isActive ? "default" : "secondary"}>
                  {serviceOption.isActive ? "Active" : "Inactive"}
                </Badge>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-muted-foreground">Target Audience</label>
                  <p className="flex items-center gap-2">
                    <Badge variant="outline">{serviceOption.targetAudience}</Badge>
                  </p>
                </div>
                <div>
                  <label className="font-medium text-muted-foreground">Pricing Model</label>
                  <p className="flex items-center gap-2">
                    <Badge variant="outline">{serviceOption.pricingModel}</Badge>
                  </p>
                </div>
              </div>

              {serviceOption.description && (
                <div>
                  <label className="font-medium text-muted-foreground">Description</label>
                  <p className="mt-1">{serviceOption.description}</p>
                </div>
              )}

              {serviceOption.notes && (
                <div>
                  <label className="font-medium text-muted-foreground">Notes</label>
                  <p className="mt-1">{serviceOption.notes}</p>
                </div>
              )}
            </div>

            {/* Pricing & Timing */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Pricing & Timing</h3>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-muted-foreground">Base Price</label>
                  <p className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    {getPricingDisplay()}
                  </p>
                </div>
                <div>
                  <label className="font-medium text-muted-foreground">Currency</label>
                  <p>{serviceOption.currency || 'AED'}</p>
                </div>
                {serviceOption.duration && (
                  <>
                    <div>
                      <label className="font-medium text-muted-foreground">Duration</label>
                      <p className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {serviceOption.duration} minutes
                      </p>
                    </div>
                    <div></div>
                  </>
                )}
              </div>
            </div>

            {/* Capacity */}
            {(serviceOption.minCapacity || serviceOption.maxCapacity) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Capacity</h3>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  {serviceOption.minCapacity && (
                    <div>
                      <label className="font-medium text-muted-foreground">Minimum Capacity</label>
                      <p className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {serviceOption.minCapacity}
                      </p>
                    </div>
                  )}
                  {serviceOption.maxCapacity && (
                    <div>
                      <label className="font-medium text-muted-foreground">Maximum Capacity</label>
                      <p className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {serviceOption.maxCapacity}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Discount Information */}
            {serviceOption.discountType && serviceOption.discountValue && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Discount</h3>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="font-medium text-muted-foreground">Discount Type</label>
                    <p className="flex items-center gap-2">
                      <Badge variant="outline">{serviceOption.discountType}</Badge>
                    </p>
                  </div>
                  <div>
                    <label className="font-medium text-muted-foreground">Discount Value</label>
                    <p className="flex items-center gap-2">
                      {serviceOption.discountType === 'Percentage' ? (
                        <>
                          <Percent className="h-4 w-4" />
                          {serviceOption.discountValue}%
                        </>
                      ) : (
                        <>
                          <DollarSign className="h-4 w-4" />
                          {formatPrice(serviceOption.discountValue)}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Claim Settings */}
            {(serviceOption.claimLimit || serviceOption.claimFrequency) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Claim Settings</h3>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  {serviceOption.claimLimit && (
                    <div>
                      <label className="font-medium text-muted-foreground">Claim Limit</label>
                      <p className="flex items-center gap-2">
                        <Gift className="h-4 w-4" />
                        <Badge variant="secondary">{serviceOption.claimLimit}</Badge>
                      </p>
                    </div>
                  )}
                  {serviceOption.claimFrequency && (
                    <div>
                      <label className="font-medium text-muted-foreground">Claim Frequency</label>
                      <p className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <Badge variant="secondary">{serviceOption.claimFrequency}</Badge>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Photos */}
            {photos.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Photos</h3>
                </div>

                <PhotoGallery
                  photos={photos}
                  parentType={PhotoParentType.ServiceOption}
                  parentId={serviceOption.id}
                  editable={false}
                />
              </div>
            )}

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Additional Information</h3>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-muted-foreground">Sort Order</label>
                  <p>{serviceOption.sortOrder || 0}</p>
                </div>
                <div>
                  <label className="font-medium text-muted-foreground">Status</label>
                  <p>
                    <Badge variant={serviceOption.isActive ? "default" : "secondary"}>
                      {serviceOption.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-muted-foreground">Created</label>
                  <p>{new Date(serviceOption.createdAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="font-medium text-muted-foreground">Updated</label>
                  <p>{new Date(serviceOption.updatedAt).toLocaleDateString()}</p>
                </div>
              </div>

              {serviceOption.service && (
                <div>
                  <label className="font-medium text-muted-foreground">Service</label>
                  <p className="mt-1">{serviceOption.service.name} (ID: {serviceOption.service.id})</p>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <EditServiceOptionModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        serviceOption={serviceOption}
      />
    </>
  );
}